// ExportDialogs.js
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  LinearProgress,
  Typography,
  Box,
  Fade,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Export Warning Dialog Component
export const ExportWarningDialog = ({
  open,
  onClose,
  onConfirm,
  recordCount,
  criticalThreshold = 200,
}) => {
  const isCritical = recordCount > criticalThreshold;
  const estimatedTime = Math.ceil(recordCount / 500); // Roughly 500 records per second

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningIcon color={isCritical ? "error" : "warning"} />
        Large Export Warning
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          You are about to export{" "}
          <strong>{recordCount.toLocaleString()}</strong> records.
        </DialogContentText>

        {isCritical && (
          <Alert severity="error" sx={{ my: 2 }}>
            This is a very large export that may take significant time and could
            impact browser performance.
          </Alert>
        )}

        <Box sx={{ my: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Estimated time:</strong> {estimatedTime} seconds
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Recommended:</strong>{" "}
            {isCritical
              ? "Consider adding filters to reduce the number of records"
              : "This export should complete without issues"}
          </Typography>
        </Box>

        <DialogContentText variant="body2" color="text.secondary">
          Tip: For better performance, try filtering by date range or specific
          audit types.
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isCritical ? "error" : "primary"}
        >
          Proceed with Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Smooth Linear Progress Component
const SmoothLinearProgress = ({ value }) => {
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    // Smoothly animate to the new value
    const timer = setTimeout(() => {
      setDisplayValue(value);
    }, 50);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <LinearProgress
      variant="determinate"
      value={displayValue}
      sx={{
        my: 2,
        height: 10,
        borderRadius: 5,
        transition: "transform 0.3s ease-in-out",
        "& .MuiLinearProgress-bar": {
          transition: "transform 0.3s ease-in-out",
        },
      }}
    />
  );
};

// Export Progress Dialog Component
export const ExportProgressDialog = ({
  open,
  progress,
  recordCount,
  error,
  onClose,
}) => {
  const [stage, setStage] = React.useState("");

  React.useEffect(() => {
    if (progress < 20) {
      setStage("Fetching notes from database...");
    } else if (progress >= 20 && progress < 90) {
      setStage("Generating Excel file...");
    } else if (progress >= 90 && progress < 100) {
      setStage("Finalizing export...");
    } else if (progress === 100) {
      setStage("Export complete!");
    }
  }, [progress]);

  const isComplete = progress === 100;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!isComplete && !error}
      onClose={isComplete || error ? onClose : undefined}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isComplete && <CheckCircleIcon color="success" />}
        {isComplete ? "Export Complete" : "Exporting Data..."}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ my: 2 }}>
          {!isComplete && (
            <Typography variant="body2" gutterBottom>
              Processing {recordCount.toLocaleString()} records...
            </Typography>
          )}

          <SmoothLinearProgress value={progress} />

          <Typography
            variant="body2"
            color={isComplete ? "success.main" : "text.secondary"}
            align="center"
            sx={{ fontWeight: isComplete ? "bold" : "normal" }}
          >
            {progress}% Complete
          </Typography>
        </Box>

        <Fade in={!!stage} timeout={300}>
          <Typography
            variant="caption"
            color={isComplete ? "success.main" : "text.secondary"}
            sx={{ display: "block", textAlign: "center", mt: 1 }}
          >
            {stage}
          </Typography>
        </Fade>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {isComplete && !error && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Successfully exported {recordCount.toLocaleString()} records!
          </Alert>
        )}
      </DialogContent>

      {(isComplete || error) && (
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

// Record Count Display Component
export const RecordCountDisplay = ({
  count,
  warningThreshold = 100,
  criticalThreshold = 200,
}) => {
  const getCountColor = () => {
    if (count > criticalThreshold) return "error";
    if (count > warningThreshold) return "warning";
    return "primary";
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <Typography variant="body1">
        Total Records: <strong>{count.toLocaleString()}</strong>
      </Typography>
      {count > warningThreshold && (
        <Fade in timeout={300}>
          <Alert severity={getCountColor()} sx={{ py: 0, px: 1 }}>
            Large dataset - export may take time
          </Alert>
        </Fade>
      )}
    </Box>
  );
};
